import ApiClient from "../apiClient";

export type BookmarkReviewStatus =
  | "pending"
  | "not_eligible"
  | "promoted"
  | "error";

export interface BookmarkRecord {
  id: string;
  userId: string;
  sourceType: string;
  sourceId: string;
  title: string;
  summary: string | null;
  url: string | null;
  imageUrl: string | null;
  ivScore: number | null;
  metadata: Record<string, unknown>;
  riskSignalReviewStatus: BookmarkReviewStatus;
  riskSignalReviewReason: string | null;
  riskSignalReviewedAt: string | null;
  riskSignalPromotedAt: string | null;
  riskSignalImageUrl: string | null;
  riskSignalImageFreshnessStatus: string | null;
  riskSignalImageUnavailableReason: string | null;
  riskSignalImageCheckedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookmarkInput {
  sourceType: string;
  sourceId: string;
  title: string;
  summary?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  ivScore?: number | null;
  metadata?: Record<string, unknown>;
}

export interface BookmarkPatch {
  title?: string;
  summary?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  ivScore?: number | null;
  metadata?: Record<string, unknown>;
}

export interface BookmarksService {
  list(options?: {
    sourceType?: string;
    limit?: number;
  }): Promise<BookmarkRecord[]>;
  get(id: string): Promise<BookmarkRecord>;
  save(input: BookmarkInput): Promise<BookmarkRecord>;
  update(id: string, patch: BookmarkPatch): Promise<BookmarkRecord>;
  delete(id: string): Promise<boolean>;
}

function queryString(options?: {
  sourceType?: string;
  limit?: number;
}): string {
  const params = new URLSearchParams();
  if (options?.sourceType) params.set("sourceType", options.sourceType);
  if (options?.limit) params.set("limit", String(options.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function createBookmarksService(client: ApiClient): BookmarksService {
  return {
    async list(options) {
      const json = await client.get<{ items: BookmarkRecord[] }>(
        `/api/bookmarks${queryString(options)}`,
      );
      return json.items ?? [];
    },

    async get(id) {
      const json = await client.get<{ bookmark: BookmarkRecord }>(
        `/api/bookmarks/${encodeURIComponent(id)}`,
      );
      return json.bookmark;
    },

    async save(input) {
      const json = await client.post<{ bookmark: BookmarkRecord }>(
        "/api/bookmarks",
        input,
      );
      return json.bookmark;
    },

    async update(id, patch) {
      const json = await client.patch<{ bookmark: BookmarkRecord }>(
        `/api/bookmarks/${encodeURIComponent(id)}`,
        patch,
      );
      return json.bookmark;
    },

    async delete(id) {
      const json = await client.delete<{ ok: boolean }>(
        `/api/bookmarks/${encodeURIComponent(id)}`,
      );
      return Boolean(json.ok);
    },
  };
}
